import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../components/Layout/MainLayout';
import AttendanceCorrectionModal from '../components/AttendanceCorrectionModal';
import attendanceService from '../services/attendanceService';
import attendanceCorrectionService from '../services/attendanceCorrectionService';
import shiftService from '../services/shiftService';
import { MdEdit, MdHistory } from 'react-icons/md';
import PunchOutGuardModal from '../components/DailyReport/organisms/PunchOutGuardModal';

const MyAttendancePage = () => {
  const { user } = useAuth();
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [currentShift, setCurrentShift] = useState(null);
  const [nextShift, setNextShift] = useState(null);
  const [shiftTimeline, setShiftTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [monthlyHoursSummary, setMonthlyHoursSummary] = useState(null);
  
  // Correction request states
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [selectedAttendance, setSelectedAttendance] = useState(null);
  const [correctionRequests, setCorrectionRequests] = useState([]);
  const [showRequestsHistory, setShowRequestsHistory] = useState(false);

  // Daily Report guard modal
  const [showReportGuard, setShowReportGuard] = useState(false);

  useEffect(() => {
    if (user?.role === 'HR') {
      fetchMyAttendance();
      fetchMyShift();
      fetchMyNextShift();
      fetchMyShiftTimeline();
      fetchCorrectionRequests();
    }
  }, [user, selectedMonth, selectedYear]);

  const fetchMyAttendance = async () => {
    try {
      setLoading(true);
      const [attendanceResponse, summaryResponse] = await Promise.all([
        attendanceService.getMyAttendanceWithLeaves({
          month: selectedMonth,
          year: selectedYear
        }),
        attendanceService.getMyMonthlySummary({
          month: selectedMonth,
          year: selectedYear
        })
      ]);
      
      if (attendanceResponse.success) {
        console.log('Attendance data received:', attendanceResponse.data);
        setTodayAttendance(attendanceResponse.data.todayAttendance);
        setAttendanceRecords(attendanceResponse.data.records);
        if (attendanceResponse.data.currentShift) {
          setCurrentShift(attendanceResponse.data.currentShift);
        }
      }

      if (summaryResponse.success) {
        setMonthlyHoursSummary(summaryResponse.data);
      } else {
        setMonthlyHoursSummary(null);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load attendance data');
      setMonthlyHoursSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyShift = async () => {
    try {
      const response = await shiftService.getMyShift();
      if (response.success && response.data) {
        setCurrentShift(response.data);
      }
    } catch (err) {
      console.error('Failed to load shift data:', err);
      // Don't show error for shift data as it's not critical
    }
  };

  const fetchMyNextShift = async () => {
    try {
      const response = await shiftService.getMyNextShift();
      if (response.success) {
        setNextShift(response.data || null);
      }
    } catch (err) {
      console.error('Failed to load next shift data:', err);
    }
  };

  const fetchMyShiftTimeline = async () => {
    try {
      const response = await shiftService.getMyShiftTimeline();
      if (response.success && Array.isArray(response.data)) {
        setShiftTimeline(response.data);
      } else {
        setShiftTimeline([]);
      }
    } catch (err) {
      console.error('Failed to load shift timeline:', err);
      setShiftTimeline([]);
    }
  };

  const handlePunchIn = async () => {
    try {
      setActionLoading(true);
      setError('');
      setSuccess('');
      
      const response = await attendanceService.punchIn();
      
      if (response.success) {
        setSuccess('Punched in successfully!');
        setTodayAttendance(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to punch in');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePunchOut = async () => {
    try {
      setActionLoading(true);
      setError('');
      setSuccess('');
      
      const response = await attendanceService.punchOut();
      
      if (response.success) {
        setSuccess('Punched out successfully!');
        setTodayAttendance(response.data);
      }
    } catch (err) {
      // If backend blocked punch-out because daily report is missing, show guard modal
      const errMsg = err.response?.data?.error || '';
      if (
        err.response?.status === 403 &&
        errMsg.toLowerCase().includes('daily report')
      ) {
        setShowReportGuard(true);
      } else {
        setError(errMsg || 'Failed to punch out');
      }
    } finally {
      setActionLoading(false);
    }
  };

  // Called by PunchOutGuardModal after the report is submitted successfully.
  // Auto-retries the punch-out so the HR doesn't have to click again.
  const handleReportSubmittedRetryPunchOut = async () => {
    setShowReportGuard(false);
    try {
      setActionLoading(true);
      setError('');
      setSuccess('');
      const response = await attendanceService.punchOut();
      if (response.success) {
        setSuccess('Report submitted and punched out successfully!');
        setTodayAttendance(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to punch out after report submission.');
    } finally {
      setActionLoading(false);
    }
  };

  // Correction request functions
  const fetchCorrectionRequests = async () => {
    try {
      const response = await attendanceCorrectionService.getHRCorrectionRequests();
      if (response.success) {
        setCorrectionRequests(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch correction requests:', err);
    }
  };

  const handleRequestCorrection = (attendanceRecord) => {
    setSelectedAttendance(attendanceRecord);
    setShowCorrectionModal(true);
  };

  const handleCorrectionSuccess = (message) => {
    setSuccess(message);
    fetchCorrectionRequests(); // Refresh the requests list
    setTimeout(() => setSuccess(''), 5000);
  };

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const calculateTotalHours = (punchIn, punchOut) => {
    if (!punchIn || !punchOut) return '-';
    const diffMs = new Date(punchOut).getTime() - new Date(punchIn).getTime();
    const hours = Math.round((diffMs / (1000 * 60 * 60)) * 100) / 100;
    return `${hours}h`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status, detailedStatus) => {
    // Use detailed status for display if available
    const displayStatus = detailedStatus?.displayStatus || status;
    
    const colors = {
      'WeekOff': 'bg-orange-200 text-orange-800 border-orange-200',
      'Present': 'bg-green-100 text-green-800 border-green-200',
      'Late': 'bg-orange-100 text-orange-800 border-orange-200',
      'Late In': 'bg-orange-100 text-orange-800 border-orange-200',
      'Early Out': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Late In & Early Out': 'bg-red-100 text-red-800 border-red-200',
      'Absent': 'bg-red-100 text-red-800 border-red-200',
      'HalfDay': 'bg-blue-100 text-blue-800 border-blue-200',
      'OnLeave': 'bg-purple-100 text-purple-800 border-purple-200',
      'HalfDayLeave': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'EarlyOut': 'bg-yellow-100 text-yellow-800 border-yellow-200'
    };
    return colors[displayStatus] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const toDateKey = (dateValue) => {
    if (!dateValue) return '';

    if (typeof dateValue === 'string') {
      const trimmed = dateValue.trim();
      if (!trimmed) return '';
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return trimmed;
      }

      const parsedFromString = new Date(trimmed);
      if (Number.isNaN(parsedFromString.getTime())) return '';
      const year = parsedFromString.getFullYear();
      const month = (parsedFromString.getMonth() + 1).toString().padStart(2, '0');
      const day = parsedFromString.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) return '';
    const year = parsedDate.getFullYear();
    const month = (parsedDate.getMonth() + 1).toString().padStart(2, '0');
    const day = parsedDate.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isDateWithinAssignment = (dateKey, assignment) => {
    if (!dateKey || !assignment || !assignment.startDate) return false;

    const startDateKey = toDateKey(assignment.startDate);
    if (!startDateKey) return false;

    const endDateKey = assignment.endDate ? toDateKey(assignment.endDate) : '';
    if (endDateKey) {
      return dateKey >= startDateKey && dateKey <= endDateKey;
    }

    return dateKey >= startDateKey;
  };

  const getShiftCode = (shiftName) => {
    if (!shiftName || typeof shiftName !== 'string') return '';

    const words = shiftName
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    if (words.length >= 2) {
      return `${words[0][0] || ''}${words[1][0] || ''}`.toUpperCase();
    }

    return words[0].slice(0, 2).toUpperCase();
  };

  const formatCompactShiftTimePart = (timeValue) => {
    if (!timeValue || typeof timeValue !== 'string') return '';

    const [hoursRaw, minutesRaw] = timeValue.split(':');
    const hours = Number(hoursRaw);
    const minutes = Number(minutesRaw || '0');

    if (Number.isNaN(hours) || Number.isNaN(minutes)) return '';

    const period = hours >= 12 ? 'P' : 'A';
    const hour12 = (hours % 12) || 12;
    const minuteText = minutes ? `:${minutes.toString().padStart(2, '0')}` : '';

    return `${hour12}${minuteText}${period}`;
  };

  const formatCompactShiftTimeRange = (shift) => {
    if (!shift?.startTime || !shift?.endTime) return '';

    const startPart = formatCompactShiftTimePart(shift.startTime);
    const endPart = formatCompactShiftTimePart(shift.endTime);
    if (!startPart || !endPart) return '';

    return `${startPart}-${endPart}`;
  };

  const getShiftForDate = (dateKey) => {
    if (!dateKey) return null;

    const timelineAssignments = Array.isArray(shiftTimeline) ? shiftTimeline : [];
    const fallbackAssignments = [currentShift, nextShift].filter(Boolean);
    const assignmentPool = timelineAssignments.length > 0
      ? [...timelineAssignments, ...fallbackAssignments]
      : fallbackAssignments;

    const eligibleAssignments = assignmentPool
      .filter((assignment) => isDateWithinAssignment(dateKey, assignment))
      .sort((a, b) => {
        const aStart = toDateKey(a.startDate);
        const bStart = toDateKey(b.startDate);
        return bStart.localeCompare(aStart);
      });

    if (eligibleAssignments.length > 0) {
      return eligibleAssignments[0];
    }

    // Fallback for responses where date range is not included but shift exists.
    const undatedAssignments = assignmentPool
      .filter(Boolean)
      .filter((assignment) => !assignment.startDate && (assignment.shift?.shiftName || assignment.shiftType));

    if (undatedAssignments.length > 0) {
      return undatedAssignments[0];
    }

    return null;
  };

  const getShiftPreviewInfo = (assignment) => {
    if (!assignment) return null;

    const shiftName = assignment.shift?.shiftName || assignment.shiftType || '';
    if (!shiftName) return null;

    const shiftTimeLabel = formatCompactShiftTimeRange(assignment.shift);
    const fullTiming = assignment.shift?.formattedTiming || shiftTimeLabel || '';

    return {
      code: getShiftCode(shiftName),
      timeLabel: shiftTimeLabel,
      fullName: shiftName,
      fullTiming
    };
  };

  const generateCalendarDays = () => {
    const year = selectedYear;
    const month = selectedMonth - 1; // JavaScript months are 0-indexed
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${(month + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      const dayDate = new Date(year, month, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayKey = toDateKey(today);
      const isToday = dayDate.toDateString() === today.toDateString();
      const isSunday = dayDate.getDay() === 0;
      const isPastDate = dayDate < today;
      const isFutureDate = dayDate > today;

      const attendanceRecord = attendanceRecords.find(record => {
        const recordDateKey = toDateKey(record.date);
        const matches = recordDateKey === dateStr;
        
        // Debug logging for today's date
        if (dateStr === todayKey) {
          console.log('Today date check:', {
            dateStr,
            recordDateKey,
            matches,
            record,
            todayAttendance
          });
        }
        
        return matches;
      });
      
      // If no record found but it's today and we have todayAttendance, use that
      let finalAttendanceRecord = attendanceRecord;
      if (!attendanceRecord && dateStr === todayKey && todayAttendance) {
        finalAttendanceRecord = todayAttendance;
        console.log('Using todayAttendance for calendar:', todayAttendance);
      }

      // Build synthetic absent rows so correction can be requested directly from absent dates.
      if (!finalAttendanceRecord && isPastDate && !isToday && !isSunday) {
        finalAttendanceRecord = {
          _id: `absent_${dateStr}`,
          date: dateStr,
          status: 'Absent',
          punchIn: null,
          punchOut: null,
          totalHours: 0,
          remarks: 'Auto-generated absent day',
          isAbsentPlaceholder: true
        };
      }

      let shiftPreview = null;
      if (!finalAttendanceRecord && isFutureDate && !isSunday) {
        const assignedShift = getShiftForDate(dateStr);
        shiftPreview = getShiftPreviewInfo(assignedShift);
      }
      
      days.push({
        day,
        date: dateStr,
        attendance: finalAttendanceRecord,
        isWeekOff: isSunday,
        shiftPreview
      });
    }
    
    return days;
  };

  if (user?.role !== 'HR') {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-center py-12"> 
            <h2 className="text-xl font-semibold text-gray-600">Access Denied</h2>
            <p className="text-gray-500 mt-2">This page is only accessible to HR employees.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  const calendarDays = generateCalendarDays();
  const absentCount = calendarDays.filter(
    (dayData) => dayData && dayData.attendance?.status === 'Absent'
  ).length;

  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Attendance</h1>
          <p className="text-gray-600">Track your daily attendance and view your records</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
            {success}
          </div>
        )}

        {/* My Shift Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">My Duty Schedule</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-600 font-medium">Assigned Shift</p>
              <p className="text-lg font-semibold text-blue-900">
                {currentShift?.shift?.shiftName || 'Not yet assigned'}
              </p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-green-600 font-medium">Shift Timing</p>
              <p className="text-lg font-semibold text-green-900">
                {currentShift?.shift?.formattedTiming || 'N/A'}
              </p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-sm text-purple-600 font-medium">Grace Period</p>
              <p className="text-lg font-semibold text-purple-900">
                {currentShift?.shift?.gracePeriod || 0} minutes
              </p>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <p className="text-sm text-orange-600 font-medium">Next Shift</p>
              <p className="text-base font-semibold text-orange-900">
                {nextShift?.shift?.shiftName || nextShift?.shiftType || 'Not yet assigned'}
              </p>
              <p className="text-sm text-orange-700 mt-1">
                {nextShift?.startDate ? `Starts on ${formatDate(nextShift.startDate)}` : '-'}
              </p>
            </div>
          </div>
          
          {currentShift?.shift?.isNightShift && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <span className="font-medium">Night Shift:</span> This shift crosses midnight. 
                Your attendance will be calculated accordingly.
              </p>
            </div>
          )}
        </div>

        {!currentShift && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            <p className="font-medium">No Shift Assigned</p>
            <p className="text-sm">Please contact HRO to assign you a work shift before marking attendance.</p>
          </div>
        )}

        {/* Today's Attendance Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Today's Attendance</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Punch In</p>
              <p className="text-lg font-semibold text-gray-900">
                {formatTime(todayAttendance?.punchIn)}
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Punch Out</p>
              <p className="text-lg font-semibold text-gray-900">
                {formatTime(todayAttendance?.punchOut)}
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Total Hours</p>
              <p className="text-lg font-semibold text-gray-900">
                {todayAttendance?.totalHours ? `${todayAttendance.totalHours}h` : 
                 calculateTotalHours(todayAttendance?.punchIn, todayAttendance?.punchOut)}
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Status</p>
              {todayAttendance?.status && (
                <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(todayAttendance.status, todayAttendance.detailedStatus)}`}>
                  {todayAttendance.detailedStatus?.displayStatus || todayAttendance.status}
                </span>
              )}
            </div>
          </div>

          <div className="flex gap-4">
            <button
              onClick={handlePunchIn}
              disabled={actionLoading || todayAttendance?.punchIn || !currentShift}
              className="bg-green-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              title={!currentShift ? 'No shift assigned' : ''}
            >
              {actionLoading ? 'Processing...' : 'Punch In'}
            </button>
            <button
              onClick={handlePunchOut}
              disabled={actionLoading || !todayAttendance?.punchIn || todayAttendance?.punchOut || !currentShift}
              className="bg-red-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {actionLoading ? 'Processing...' : 'Punch Out'}
            </button>
          </div>
        </div>

        {/* Monthly Calendar */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Attendance Calendar</h2>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowRequestsHistory(!showRequestsHistory)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <MdHistory size={16} />
                <span>Correction Requests ({correctionRequests.length})</span>
              </button>
              
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="border border-gray-300 rounded-lg px-3 py-2"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(0, i).toLocaleString('en-US', { month: 'long' })}
                  </option>
                ))}
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="border border-gray-300 rounded-lg px-3 py-2"
              >
                {Array.from({ length: 5 }, (_, i) => {
                  const year = new Date().getFullYear() - 2 + i;
                  return (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {/* Total Hours Summary */}
          {monthlyHoursSummary && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-100 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Total Hours Summary</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white p-3 rounded-md border border-blue-100">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Expected Hours</p>
                  <p className="text-xl font-bold text-blue-700">{monthlyHoursSummary.expectedHours}h</p>
                </div>
                <div className="bg-white p-3 rounded-md border border-blue-100">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Leaves Taken</p>
                  <p className="text-xl font-bold text-yellow-700">{monthlyHoursSummary.leaveDays} day{monthlyHoursSummary.leaveDays === 1 ? '' : 's'}</p>
                </div>
                <div className="bg-white p-3 rounded-md border border-blue-100">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Adjusted Expected Hours</p>
                  <p className="text-xl font-bold text-indigo-700">{monthlyHoursSummary.adjustedExpectedHours}h</p>
                </div>
                <div className="bg-white p-3 rounded-md border border-blue-100">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Worked Hours</p>
                  <p className="text-xl font-bold text-green-700">{monthlyHoursSummary.workedHours}h</p>
                </div>
              </div>
            </div>
          )}

          {/* Correction Requests History */}
          {showRequestsHistory && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Correction Requests History</h3>
              {correctionRequests.length > 0 ? (
                <div className="space-y-3">
                  {correctionRequests.slice(0, 5).map((request) => (
                    <div key={request._id} className="flex items-center justify-between p-3 bg-white rounded border">
                      <div>
                        <p className="font-medium text-gray-900">
                          {request.date} - {request.reason.substring(0, 50)}...
                        </p>
                        <p className="text-sm text-gray-600">
                          Status: <span className={`font-medium ${
                            request.status === 'Approved' ? 'text-green-600' :
                            request.status === 'Rejected' ? 'text-red-600' : 'text-yellow-600'
                          }`}>
                            {request.status}
                          </span>
                          {request.status !== 'Pending' && request.response?.reviewedAt && (
                            <span className="ml-2">
                              on {new Date(request.response.reviewedAt).toLocaleDateString()}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="text-sm text-gray-500">
                        {request.priority} Priority
                      </div>
                    </div>
                  ))}
                  {correctionRequests.length > 5 && (
                    <p className="text-sm text-gray-600 text-center">
                      Showing latest 5 requests. Total: {correctionRequests.length}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-gray-600">No correction requests found.</p>
              )}
            </div>
          )}

          {/* Monthly Summary */}
          {attendanceRecords.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">
                  {attendanceRecords.filter(r => r.status === 'Present').length}
                </p>
                <p className="text-sm text-gray-600">Present</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-orange-600">
                  {attendanceRecords.filter(r => r.detailedStatus?.isLateIn || r.status === 'Late').length}
                </p>
                <p className="text-sm text-gray-600">Late In</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-600">
                  {attendanceRecords.filter(r => r.detailedStatus?.isEarlyOut || r.status === 'EarlyOut').length}
                </p>
                <p className="text-sm text-gray-600">Early Out</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">
                  {attendanceRecords.filter(r => r.status === 'HalfDay').length}
                </p>
                <p className="text-sm text-gray-600">Half Day</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-600">
                  {attendanceRecords.filter(r => r.status === 'OnLeave').length}
                </p>
                <p className="text-sm text-gray-600">Full Day Leave</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-red-600">{absentCount}</p>
                <p className="text-sm text-gray-600">Absent</p>
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading calendar...</p>
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-1">
              {/* Calendar Headers */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="p-2 text-center font-medium text-gray-600 bg-gray-50">
                  {day}
                </div>
              ))}
              
              {/* Calendar Days */}
              {calendarDays.map((dayData, index) => (
                <div key={index} className="aspect-square border border-gray-200 p-1">
                  {dayData && (
                    <div className="h-full flex flex-col">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-900">{dayData.day}</span>
                        {dayData.attendance && !dayData.attendance.isLeave && !dayData.isWeekOff && (
                          <button
                            onClick={() => handleRequestCorrection(dayData.attendance)}
                            className="text-blue-600 hover:text-blue-800 p-1"
                            title="Request correction"
                          >
                            <MdEdit size={12} />
                          </button>
                        )}
                      </div>
                      {dayData.attendance ? (
                        <div 
                          className={`flex-1 mt-1 rounded text-xs text-center py-1 cursor-pointer border ${getStatusColor(dayData.attendance.status, dayData.attendance.detailedStatus)}`}
                          title={dayData.attendance.isLeave ? 
                            `${dayData.attendance.leaveTypeName || dayData.attendance.status} - ${dayData.attendance.leaveReason}` :
                            `${dayData.attendance.detailedStatus?.displayStatus || dayData.attendance.status} - In: ${formatTime(dayData.attendance.punchIn)} Out: ${formatTime(dayData.attendance.punchOut)} Hours: ${dayData.attendance.totalHours || calculateTotalHours(dayData.attendance.punchIn, dayData.attendance.punchOut)}`
                          }
                        >
                          <div className="font-medium">
                            {dayData.attendance.status === 'OnLeave' ? (dayData.attendance.leaveTypeName || 'Full Leave') : 
                             dayData.attendance.status === 'HalfDayLeave' ? `${dayData.attendance.leaveTypeName || 'Half Leave'} (Half)` :
                             dayData.attendance.detailedStatus?.displayStatus || dayData.attendance.status}
                          </div>
                          {dayData.attendance.isLeave ? (
                            <div className="text-[10px] mt-1 opacity-75">
                              🏖️ {dayData.attendance.leaveType === 'HALF_DAY' ? '4h' : '8h'}
                            </div>
                          ) : (
                            (dayData.attendance.totalHours > 0) && (
                              <div className="text-[10px] mt-1">
                                {dayData.attendance.totalHours}h
                              </div>
                            )
                          )}
                        </div>
                      ) : dayData.isWeekOff ? (
                        <div className={`flex-1 mt-1 rounded text-xs text-center py-1 border ${getStatusColor('WeekOff')}`}>
                          Week Off
                        </div>
                      ) : dayData.shiftPreview ? (
                        <div
                          className="flex-1 mt-1 rounded text-xs text-center py-1 border bg-slate-50 text-slate-700 border-slate-200"
                          title={`${dayData.shiftPreview.fullName}${dayData.shiftPreview.fullTiming ? ` - ${dayData.shiftPreview.fullTiming}` : ''}`}
                        >
                          <div className="font-semibold text-[11px] leading-tight truncate">
                            {dayData.shiftPreview.code}
                          </div>
                          {dayData.shiftPreview.timeLabel && (
                            <div className="text-[10px] mt-1 leading-tight truncate">
                              {dayData.shiftPreview.timeLabel}
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Legend */}
          <div className="mt-6 flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-100 border border-green-200 rounded"></div>
              <span>Present</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-orange-100 border border-orange-200 rounded"></div>
              <span>Late In</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-yellow-100 border border-yellow-200 rounded"></div>
              <span>Early Out</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-100 border border-red-200 rounded"></div>
              <span>Absent</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-100 border border-blue-200 rounded"></div>
              <span>Half Day</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-purple-100 border border-purple-200 rounded"></div>
              <span>Full Day Leave</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-orange-100 border border-orange-200 rounded"></div>
              <span>Week Off (Sunday)</span>
            </div>
            <div className="text-xs text-gray-500 ml-4">
              * Hover over calendar days to see detailed information
            </div>
          </div>
        </div>
      </div>

      {/* Attendance Correction Modal */}
      <AttendanceCorrectionModal
        isOpen={showCorrectionModal}
        onClose={() => setShowCorrectionModal(false)}
        attendanceRecord={selectedAttendance}
        onSuccess={handleCorrectionSuccess}
      />

      {/* Daily Report Guard Modal – shown when punch-out is blocked */}
      <PunchOutGuardModal
        isOpen={showReportGuard}
        onReportSubmitted={handleReportSubmittedRetryPunchOut}
      />
    </MainLayout>
  );
};

export default MyAttendancePage;